import { SPACING } from "@/constants/layout"
import { Card, CardContent } from "@/components/ui/card"
import { SendHorizontal, QrCode, Wallet, Globe, Repeat, BarChart3, Bot, CreditCard } from "lucide-react"
import { useVerticalSolutionsContext } from './VerticalSolutionsPage'
import { BreadcrumbNav, BreadcrumbItem } from '@/components/ui/breadcrumb-nav'

const features = [
    {
        icon: SendHorizontal,
        title: "Fast Global Transfers",
        description: "Send money internationally in seconds with near-zero fees using stablecoin technology."
    },
    {
        icon: QrCode,
        title: "Shareable Payment Links",
        description: "Create custom QR codes and payment links for specific amounts to receive money instantly."
    },
    {
        icon: Wallet,
        title: "Multi-Source Funding",
        description: "Fund transfers from your bank account or crypto wallet with seamless integration."
    },
    {
        icon: BarChart3,
        title: "Spending Insights",
        description: "Track and analyze your transactions across bank accounts and Tinta balance."
    },
    {
        icon: Repeat,
        title: "Smart Automation",
        description: "Set rules to automatically route funds based on triggers, schedules, or specific goals."
    },
    {
        icon: Bot,
        title: "WhatsApp AI Assistant",
        description: "Send money by chatting with our AI assistant directly in WhatsApp."
    },
    {
        icon: CreditCard,
        title: "Digital Payout Card",
        description: "Receive gig economy payouts directly to Tinta with our virtual card system."
    },
    {
        icon: Globe,
        title: "Borderless Payments",
        description: "No more currency conversion fees or waiting days for international transfers."
    }
]

const useCases = [
    {
        title: "International Remittances",
        description: "Send money home to family without the high fees and long delays of traditional services."
    },
    {
        title: "Gig Workers",
        description: "Receive payments from multiple platforms instantly to your Tinta account using our virtual card."
    },
    {
        title: "Small Businesses",
        description: "Create payment links and QR codes for customers to pay easily with minimal transaction fees."
    },
    {
        title: "Personal Finance",
        description: "Automate your savings, bill payments, and budget allocations with smart routing rules."
    }
]

export function TintaPage() {
    // Get breadcrumbs from parent
    const { parentBreadcrumbs } = useVerticalSolutionsContext()

    // Add Tinta-specific breadcrumb
    const breadcrumbs: BreadcrumbItem[] = [
        ...parentBreadcrumbs,
        {
            label: "Tinta",
            description: "The peer-to-peer payment platform using stablecoins for instant, borderless transfers"
        }
    ]

    return (
        <>
            <BreadcrumbNav items={breadcrumbs} />

            <div className="space-y-10 md:space-y-16">
                {/* Hero Section */}
                <div>
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 md:mb-6">
                            Tinta: Borderless Payments Made Simple
                        </h1>
                        <p className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400 mb-6 md:mb-8">
                            The peer-to-peer payment platform that uses stablecoins to make
                            international transfers instant, cheap, and accessible to everyone.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                            <button className="bg-primary text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-md font-medium text-sm sm:text-base">
                                Download App
                            </button>
                            <button className="border border-neutral-300 dark:border-neutral-700 px-5 py-2.5 sm:px-6 sm:py-3 rounded-md font-medium text-sm sm:text-base mt-2 sm:mt-0">
                                How It Works
                            </button>
                        </div>
                    </div>
                </div>

                {/* Overview Section */}
                <div>
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg overflow-hidden">
                            <div className="aspect-video bg-neutral-200 dark:bg-neutral-800 flex items-center justify-center">
                                <span className="text-neutral-500">Tinta App Demo</span>
                            </div>
                        </div>
                        <div className="mt-6 md:mt-8 text-center sm:text-left px-1 sm:px-0">
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 md:mb-4">Money Transfer Reimagined</h2>
                            <p className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400">
                                Tinta leverages stablecoin technology to eliminate the friction, fees, and delays
                                of traditional money transfer services. Send money across borders instantly,
                                create custom payment links, automate your finances, and integrate with your favorite
                                apps—all from one powerful, easy-to-use platform.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div>
                    <div className="text-center mb-6 md:mb-8">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 md:mb-3">Key Features</h2>
                        <p className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto px-1">
                            Everything you need for seamless money transfers and financial management
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
                        {features.map((feature) => {
                            const Icon = feature.icon
                            return (
                                <Card key={feature.title} variant="default" className="h-full">
                                    <CardContent className="p-4 sm:p-6 h-full flex flex-col">
                                        <div className="flex flex-row sm:flex-col items-start sm:items-center text-left sm:text-center gap-3 sm:gap-4 h-full">
                                            <div className="mt-1 flex-shrink-0">
                                                <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{feature.title}</h3>
                                                <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                </div>

                {/* How It Works Section */}
                <div>
                    <div className="text-center mb-6 md:mb-8">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 md:mb-3">How Tinta Works</h2>
                        <p className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto px-1">
                            Simple, secure, and lightning-fast payments
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        <div className="text-center bg-neutral-50 dark:bg-neutral-900 p-5 rounded-lg">
                            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary mb-3 sm:mb-4">
                                <span className="text-lg sm:text-xl font-bold">1</span>
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold mb-2">Connect Your Accounts</h3>
                            <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                Link your bank account, crypto wallet, or add funds directly to your Tinta balance.
                            </p>
                        </div>
                        <div className="text-center bg-neutral-50 dark:bg-neutral-900 p-5 rounded-lg">
                            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary mb-3 sm:mb-4">
                                <span className="text-lg sm:text-xl font-bold">2</span>
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold mb-2">Send or Request Money</h3>
                            <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                Transfer funds with just a phone number, create QR codes, or set up automated rules.
                            </p>
                        </div>
                        <div className="text-center bg-neutral-50 dark:bg-neutral-900 p-5 rounded-lg">
                            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary mb-3 sm:mb-4">
                                <span className="text-lg sm:text-xl font-bold">3</span>
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold mb-2">Instant Delivery</h3>
                            <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                Recipients get funds in seconds, no matter where they are in the world.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Use Cases Section */}
                <div className="py-6 bg-neutral-50 dark:bg-neutral-900 rounded-lg sm:rounded-xl">
                    <div className="text-center mb-6 md:mb-8">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 md:mb-3">Who Uses Tinta</h2>
                        <p className="text-sm sm:text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto px-1">
                            Designed for everyone who needs faster, cheaper, and smarter money transfers
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
                        {useCases.map((useCase) => (
                            <Card key={useCase.title} variant="default" className="h-full">
                                <CardContent className="p-4 sm:p-6 h-full">
                                    <h3 className="text-base sm:text-lg font-semibold mb-2">{useCase.title}</h3>
                                    <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                        {useCase.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section - Full Width */}
            <div className="mt-16 -mx-4 sm:-mx-6 md:-mx-8 lg:-mx-16">
                <div className="bg-black py-8 md:py-10">
                    <div className="max-w-3xl mx-auto text-center px-4">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-white">Ready to Start Sending Money Globally?</h2>
                        <p className="text-sm sm:text-base md:text-lg text-neutral-300 mb-5 sm:mb-6">
                            Start saving time and money with Tinta's borderless payment platform.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                            <button className="bg-blue-500 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-md font-medium text-sm sm:text-base">
                                Download App
                            </button>
                            <button className="border border-white border-opacity-30 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-md font-medium text-sm sm:text-base mt-2 sm:mt-0">
                                Business Solutions
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
